import React, { useState, useEffect } from "react";
import cancelIcon from "../../../assets/images/cancel-btn.svg";
import "./GroupedSelectionModal.css";

const GroupedSelectionModal = ({
  show,
  onClose,
  title = "Select Items",
  groups = [], // Array of {id, name, items: [{id, name, value}]}
  onSelect,
  savedSelections = [], // Array of {id, name, selections: []}
  onSaveSelection,
  onLoadSelection,
  onDeleteSelection,
  selectedItems = []
}) => {
  const [localSelectedItems, setLocalSelectedItems] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (show) {
      setLocalSelectedItems(selectedItems || []);
    }
  }, [show]);

  const handleItemToggle = (item) => {
    setLocalSelectedItems((prev) => {
      const exists = prev.find((i) => String(i.id) === String(item.id));
      if (exists) {
        return prev.filter((i) => String(i.id) !== String(item.id));
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAll = (groupItems) => {
    const allSelected = groupItems.every(item => 
      localSelectedItems.find(i => i.id === item.id)
    );
    
    if (allSelected) {
      setLocalSelectedItems(prev => 
        prev.filter(i => !groupItems.find(gi => gi.id === i.id))
      );
    } else {
      const newItems = groupItems.filter(item => 
        !localSelectedItems.find(i => i.id === item.id)
      );
      setLocalSelectedItems(prev => [...prev, ...newItems]);
    }
  };

  const handleConfirmSelection = () => {
    if (onSelect) {
      onSelect(localSelectedItems);
    }
    onClose();
  };

  const handleSaveSelection = () => {
    if (saveName.trim() && localSelectedItems.length > 0) {
      if (onSaveSelection) {
        onSaveSelection({
          id: Date.now().toString(),
          name: saveName.trim(),
          selections: localSelectedItems,
          createdAt: new Date().toISOString()
        });
      }
      setSaveName("");
      setShowSaveDialog(false);
    }
  };

  const handleLoadSaved = (saved) => {
    setLocalSelectedItems(saved.selections);
  };

  const isItemSelected = (item) => {
    return localSelectedItems.find(
      (i) => String(i.id) === String(item.id),
    );
  };

  const filteredGroups = searchQuery.trim() 
    ? groups.map(group => ({
        ...group,
        items: group.items.filter(item => 
          item.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(group => group.items.length > 0)
    : groups;

  if (!show) return null;

  return (
    <div className="popup holidayPopup GroupedSelectionModal">
      <div className="popup-content grouped-selection-content">
        <div className="addNursePopup addAppointment">
          <div className="modal-header-wrapper">
            <h2 className="nurseHdn">{title}</h2>
            <div className="header-search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                className="header-search-input"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={onClose}
              className="cancelBtn"
              aria-label="Close modal">
              <img src={cancelIcon} alt="Close" />
            </button>
          </div>

          <div className="grouped-selection-body">
            <div className="selection-content">
              {/* Saved Selections Section */}
              {savedSelections.length > 0 && (
                <div className="saved-selections-section">
                  <h3 className="section-title">Saved Selections</h3>
                  <div className="saved-selections-horizontal">
                    {savedSelections.map(saved => (
                      <div 
                        key={saved.id} 
                        className="saved-item-compact"
                        onClick={() => handleLoadSaved(saved)}>
                        <div className="saved-compact-info">
                          <h4 className="saved-compact-name">{saved.name}</h4>
                          <p className="saved-compact-count">
                            {saved.selections.length} item{saved.selections.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                        {onDeleteSelection && (
                          <button
                            className="delete-compact-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteSelection(saved.id);
                            }}
                            aria-label="Delete selection">
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Items Summary */}
              {localSelectedItems.length > 0 && (
                <div className="selected-summary">
                  <div className="summary-header">
                    <span className="summary-title">
                      Selected Items ({localSelectedItems.length})
                    </span>
                    <button
                      className="clear-all-btn"
                      onClick={() => setLocalSelectedItems([])}>
                      Clear All
                    </button>
                  </div>
                  <div className="selected-chips">
                    {localSelectedItems.map(item => (
                      <div key={item.id} className="selected-chip">
                        <span>{item.name}</span>
                        <button
                          onClick={() => handleItemToggle(item)}
                          className="chip-remove">
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Groups Grid */}
              <div className="groups-grid">
                {filteredGroups.map(group => (
                  <div key={group.id} className="group-card">
                    <div className="group-header">
                      <h3 className="group-title">{group.name}</h3>
                      <button
                        className="select-all-btn"
                        onClick={() => handleSelectAll(group.items)}>
                        {group.items.every(item => isItemSelected(item))
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    </div>
                    <div className="group-items">
                      {group.items.map(item => (
                        <label
                          key={item.id}
                          className={`item-checkbox ${isItemSelected(item) ? "selected" : ""}`}>
                          <input
                            type="checkbox"
                            checked={!!isItemSelected(item)}
                            onChange={() => handleItemToggle(item)}
                          />
                          <span className="checkbox-custom"></span>
                          <span className="item-label">{item.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {filteredGroups.length === 0 && (
                <div className="empty-state">
                  <p>No items found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="modal-footer">
            <div className="footer-left">
              {localSelectedItems.length > 0 && (
                <>
                  {showSaveDialog ? (
                    <div className="save-dialog">
                      <input
                        type="text"
                        className="save-input"
                        placeholder="Enter selection name..."
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSaveSelection()}
                        autoFocus
                      />
                      <button
                        className="save-confirm-btn"
                        onClick={handleSaveSelection}
                        disabled={!saveName.trim()}>
                        Save
                      </button>
                      <button
                        className="save-cancel-btn"
                        onClick={() => {
                          setShowSaveDialog(false);
                          setSaveName("");
                        }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="save-selection-btn"
                      onClick={() => setShowSaveDialog(true)}>
                      💾 Save Selection
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="footer-right">
              <button className="cancel-modal-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmSelection}
                disabled={localSelectedItems.length === 0}>
                Select ({localSelectedItems.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedSelectionModal;
